import { useState } from 'react';

import { CancelWorkshopError, type WorkshopDetail } from '../../../../api/workshops';
import { useViewport } from '../../../../hooks/useViewport';
import type { PickerViewMode } from '../../../bookings/shared/pickerViewMode';
import type { PickerWorkshop } from '../../../bookings/shared/pickerWorkshop';
import { usePickerMonth } from '../../../bookings/shared/usePickerMonth';
import { useRebookTargets } from '../../../bookings/shared/useRebookTargets';
import { useCancelWorkshop } from '../useWorkshop';

import type { CancelledBannerDetail } from './CancelledBanner';
import { useFlooredSettle } from './use-floored-settle';
import { cancelErrorMessage, formatLocalStartsAt } from './workshopCancelCopy';

/** Minimum time the in-flight spinner stays visible, so a fast cancel doesn't flash. */
export const MIN_PROCESSING_MS = 700;

export function pickedLabel(picked: PickerWorkshop): string {
  const parts = [picked.typeName, picked.venueName];
  if (picked.localStartsAt) parts.push(formatLocalStartsAt(picked.localStartsAt));
  return parts.join(' · ');
}

function toMessage(err: unknown): string {
  if (err instanceof CancelWorkshopError) return cancelErrorMessage(err.code, err.message);
  if (err instanceof Error) return err.message;
  return 'Could not cancel the workshop. Try again.';
}

/** State for the cancel flow: the targets for every confirmed booking, the
 *  picked destination, and the POST with its floored spinner. */
export function useCancelWorkshopFlow(
  workshop: WorkshopDetail,
  onCancelled: (detail: CancelledBannerDetail | null) => void,
) {
  const { isDesktop } = useViewport();
  const { month, setMonth } = usePickerMonth(workshop.localStartDatetime);
  const [viewMode, setViewMode] = useState<PickerViewMode>(() => (isDesktop ? 'calendar' : 'list'));
  const targets = useRebookTargets(null, workshop.id, month);
  const [picked, setPicked] = useState<PickerWorkshop | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const cancel = useCancelWorkshop(workshop.id);
  const { processing, start: startProcessing } = useFlooredSettle(MIN_PROCESSING_MS);

  const pending = targets.envelope?.pendingSales;
  const blocked =
    pending && (pending.liveReservations.length > 0 || pending.paymentsInFlight.length > 0)
      ? pending
      : null;
  const attendees = targets.envelope?.attendeeCount ?? 0;

  const pick = (workshopPicked: PickerWorkshop | null) => {
    setErrorMessage(null);
    setPicked(workshopPicked);
  };

  const confirm = async (reason: string) => {
    setErrorMessage(null);
    const commit = startProcessing();
    try {
      const result = await cancel.mutateAsync({ targetWorkshopId: picked?.id, reason });
      const detail = picked
        ? {
            targetLabel: pickedLabel(picked),
            notified: result.notifications.sent,
            failed: result.notifications.failed,
          }
        : null;
      commit(() => onCancelled(detail));
    } catch (err) {
      commit(() => setErrorMessage(toMessage(err)));
    }
  };

  return {
    targets,
    month,
    setMonth,
    viewMode,
    setViewMode,
    blocked,
    attendees,
    bookings: workshop.roster.activeBookingsCount,
    nothingToMove: targets.envelope !== null && attendees === 0,
    picked,
    pick,
    errorMessage,
    processing,
    confirm,
  };
}
